import * as Tone from "tone";
import { buildAudioGraph, type AudioGraph } from "./graph";
import {
  scheduleDrumStep,
  scheduleMelodyStep,
  applySegmentSettings,
  type FxOrEnvelopeParam,
  type StepPlayer,
  type StepSlice,
} from "./scheduler";
import type { KeyboardTrackId, TrackId } from "../constants/scale";
import type { DrumId } from "../constants/drums";
import type { Segment } from "../state/arrangementStore";
import { stepsPerBar } from "../constants/layout";
import { polyphonyVelocity } from "./velocity";
import { decaySeconds } from "./envelope";

const KEYBOARD_TRACKS: readonly KeyboardTrackId[] = ["keys", "bass", "lead"];
const TAIL_SECONDS = 2;

export function segmentSeconds(segment: { bars: number; bpm: number }): number {
  return (segment.bars * 240) / segment.bpm;
}

export function arrangementSeconds(segments: readonly Segment[]): number {
  return segments.reduce((acc, s) => acc + segmentSeconds(s), 0);
}

/**
 * Walk the arrangement and feed every drum + melody event to the player, each
 * tagged with an absolute offline-context time (seconds from the start of the
 * render). Extracted so it can be unit-tested with a stub player — the audio
 * graph is real only inside `renderArrangementToBuffer`.
 */
export function planArrangement(
  segments: readonly Segment[],
  player: StepPlayer,
): number {
  let cursor = 0;
  for (const seg of segments) {
    applySegmentSettings(player, seg as unknown as StepSlice);
    const segDuration = segmentSeconds(seg);
    const drumsSubs = seg.subdivisions.drums;
    const drumsPerBar = stepsPerBar(drumsSubs);
    const drumsTotal = seg.bars * drumsPerBar;
    const drumStepDur = segDuration / drumsTotal;
    for (let s = 0; s < drumsTotal; s++) {
      const stepTime = cursor + s * drumStepDur;
      const cellStep = s % drumsPerBar;
      scheduleDrumStep(player, seg as unknown as StepSlice, cellStep, stepTime);
    }
    for (const track of KEYBOARD_TRACKS) {
      const subs = seg.subdivisions[track];
      const perBar = stepsPerBar(subs);
      const totalSteps = seg.bars * perBar;
      const stepDur = segDuration / totalSteps;
      for (let s = 0; s < totalSteps; s++) {
        const stepTime = cursor + s * stepDur;
        const cellStep = s % perBar;
        scheduleMelodyStep(
          player,
          seg as unknown as StepSlice,
          track,
          cellStep,
          stepTime,
        );
      }
    }
    cursor += segDuration;
  }
  for (const track of KEYBOARD_TRACKS) {
    player.setMelodyChordAt(track, [], cursor);
  }
  return cursor;
}

function makePlayerFromGraph(graph: AudioGraph): StepPlayer {
  const heldMidis: Record<KeyboardTrackId, Set<number>> = {
    keys: new Set(),
    bass: new Set(),
    lead: new Set(),
  };
  return {
    triggerDrum(id: DrumId, time: number) {
      graph.drumVoices[id].trigger(time);
    },
    setMelodyChordAt(
      track: KeyboardTrackId,
      midis: readonly number[],
      time: number,
    ) {
      const voice = graph.synthVoices[track];
      const held = heldMidis[track];
      const next = new Set(midis);
      for (const m of held) if (!next.has(m)) voice.releaseAt(m, time);
      const velocity = polyphonyVelocity(next.size);
      for (const m of next) if (!held.has(m)) voice.attackAt(m, time, velocity);
      heldMidis[track] = next;
    },
    setBpm() {
      // Renderer encodes BPM into per-step time offsets directly.
    },
    setFx(track: TrackId, param: FxOrEnvelopeParam, value01: number) {
      if (param === "decay") {
        const seconds = decaySeconds(value01);
        if (track === "drums") {
          for (const voice of Object.values(graph.drumVoices)) {
            voice.setRelease(seconds);
          }
        } else {
          graph.synthVoices[track].setRelease(seconds);
        }
        return;
      }
      graph.buses[track].setParam(param, value01);
    },
  };
}

export async function renderArrangementToBuffer(
  segments: readonly Segment[],
): Promise<AudioBuffer> {
  if (segments.length === 0) throw new Error("Nothing to export");
  const total = arrangementSeconds(segments) + TAIL_SECONDS;

  const buffer = await Tone.Offline(async () => {
    const graph = buildAudioGraph();
    const player = makePlayerFromGraph(graph);
    await graph.ready;
    planArrangement(segments, player);
  }, total);

  const native = buffer.get();
  if (!native) throw new Error("Render produced no audio");
  return native;
}
